import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

export default function AdminCourseReviewPage() {
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get('batch');

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editValue, setEditValue] = useState('');
  const [institutions, setInstitutions] = useState([]);

  const token = localStorage.getItem('adminToken');

  const fetchCourses = () => {
    setLoading(true);
    const params = new URLSearchParams({ status: 'PENDING_REVIEW' });
    if (batchId) params.set('importBatchId', batchId);
    fetch(`${API_BASE}/admin/courses?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => setCourses(json.success ? json.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(fetchCourses, [batchId]);
  useEffect(() => {
    fetch(`${API_BASE}/admin/institutions`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((json) => setInstitutions(json.data || []));
  }, []);

  const saveInstitutionTags = async (courseId, institutionIds) => {
    await fetch(`${API_BASE}/admin/courses/${courseId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ institutionIds }) });
    fetchCourses();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelected(selected.length === courses.length ? [] : courses.map((c) => c.id));
  };

  const startEdit = (id, field, currentValue) => {
    setEditingCell({ id, field });
    setEditValue(currentValue ?? '');
  };

  const saveEdit = async () => {
    const { id, field } = editingCell;
    await fetch(`${API_BASE}/admin/courses/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ [field]: field === 'tuitionFeeAmount' || field === 'agencyApplicationFee' ? parseFloat(editValue) || null : editValue }),
    });
    setEditingCell(null);
    fetchCourses();
  };

  const handleApprove = async (id) => {
    setCourses((prev) => prev.filter((c) => c.id !== id));
    await fetch(`${API_BASE}/admin/courses/${id}/approve`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
  };

  const handleReject = async (id) => {
    setCourses((prev) => prev.filter((c) => c.id !== id));
    await fetch(`${API_BASE}/admin/courses/${id}/reject`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
  };

  const handleBulkApprove = async () => {
    await fetch(`${API_BASE}/admin/courses/bulk-approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ids: selected }),
    });
    setCourses((prev) => prev.filter((c) => !selected.includes(c.id)));
    setSelected([]);
  };

  const isIncomplete = (course) => !course.tuitionFeeAmount || !course.requirements?.length;

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AdminSidebar />
      <main className="pt-16 md:pt-2 md:ml-64 min-h-screen p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-[#00255a] mb-2">Review Extracted Courses</h2>
          <p className="text-[#64748b]">
            {courses.length} course{courses.length !== 1 ? 's' : ''} awaiting review
            {batchId ? ' from this import batch' : ''}. Correct any AI extraction errors before approving.
          </p>
        </div>

        {selected.length > 0 && (
          <div className="mb-4 flex items-center gap-4 bg-[#00255a]/5 border border-[#00255a]/10 rounded-xl p-4">
            <span className="text-sm font-semibold text-[#00255a]">{selected.length} selected</span>
            <button
              onClick={handleBulkApprove}
              className="ml-auto px-6 py-2 text-white rounded-lg font-semibold text-sm"
              style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
            >
              Approve Selected
            </button>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#f2f4f6]">
                <tr>
                  <th className="px-4 py-4 w-10">
                    <input type="checkbox" checked={selected.length === courses.length && courses.length > 0} onChange={toggleSelectAll} />
                  </th>
                  {['Course Name', 'Degree', 'Field', 'Tuition', 'Agency Fee', 'Requirements', 'Universities', ''].map((h) => (
                    <th key={h} className="px-4 py-4 text-xs uppercase text-[#64748b]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {loading ? (
                  <tr><td colSpan="8" className="p-12 text-center text-[#64748b]">Loading...</td></tr>
                ) : courses.length === 0 ? (
                  <tr><td colSpan="8" className="p-12 text-center text-[#64748b]">No courses pending review.</td></tr>
                ) : (
                  courses.map((course) => (
                    <tr key={course.id} className={`hover:bg-[#f7f9fb] transition-colors ${isIncomplete(course) ? 'border-l-4 border-l-[#ff7b7b]' : ''}`}>
                      <td className="px-4 py-4">
                        <input type="checkbox" checked={selected.includes(course.id)} onChange={() => toggleSelect(course.id)} />
                      </td>
                      <EditableCell course={course} field="courseName" editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} startEdit={startEdit} saveEdit={saveEdit} />
                      <EditableCell course={course} field="degreeLevel" editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} startEdit={startEdit} saveEdit={saveEdit} />
                      <EditableCell course={course} field="fieldOfStudy" editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} startEdit={startEdit} saveEdit={saveEdit} />
                      <EditableCell course={course} field="tuitionFeeAmount" editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} startEdit={startEdit} saveEdit={saveEdit} prefix="$" placeholder="Missing" />
                      <EditableCell course={course} field="agencyApplicationFee" editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} startEdit={startEdit} saveEdit={saveEdit} prefix="$" />
                      <td className="px-4 py-4 text-sm text-[#64748b] max-w-xs">
                        {(course.requirements || []).join(', ') || <span className="text-[#ff7b7b] italic">None extracted</span>}
                      </td>
                      <td className="px-4 py-4 min-w-48"><select multiple value={(course.institutions || []).map((link) => link.institutionId)} onChange={(e) => saveInstitutionTags(course.id, Array.from(e.target.selectedOptions).map((option) => option.value))} className="w-full border rounded p-1 text-xs h-20">{institutions.filter((institution) => institution.country === course.country).map((institution) => <option key={institution.id} value={institution.id}>{institution.name}</option>)}</select><p className="text-[10px] text-[#64748b] mt-1">Ctrl/Cmd-click for multiple</p></td>
                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <button onClick={() => handleApprove(course.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-full">
                          <span className="material-symbols-outlined text-[20px]">check_circle</span>
                        </button>
                        <button onClick={() => handleReject(course.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-full">
                          <span className="material-symbols-outlined text-[20px]">cancel</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function EditableCell({ course, field, editingCell, editValue, setEditValue, startEdit, saveEdit, prefix, placeholder }) {
  const isEditing = editingCell?.id === course.id && editingCell?.field === field;
  const value = course[field];

  if (isEditing) {
    return (
      <td className="px-4 py-4">
        <div className="flex items-center gap-1">
          <input
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
            className="w-full px-2 py-1 border border-[#00255a] rounded text-sm outline-none"
          />
          <button onClick={saveEdit} className="text-emerald-600">
            <span className="material-symbols-outlined text-[18px]">check</span>
          </button>
        </div>
      </td>
    );
  }

  return (
    <td
      onClick={() => startEdit(course.id, field, value)}
      className="px-4 py-4 text-sm text-[#0f172a] cursor-pointer hover:bg-[#f2f4f6] transition-colors"
    >
      {value ? `${prefix || ''}${value}` : <span className="text-[#ff7b7b] italic">{placeholder || 'Empty'}</span>}
    </td>
  );
}
